import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import Sidebar from './Sidebar_clean';
import TaskAssignmentNotification from './TaskAssignmentNotification';
import CrisisResourceSuggestionBanner from './CrisisResourceSuggestionBanner';
import LessonsLearnedBanner from './LessonsLearnedBanner';
import VideoCallBanner from './VideoCallBanner';
import CMTIssuesBanner from './auth/CMTIssuesBanner';
import { useNewTaskNotifications } from '../hooks/useNewTaskNotifications';
import { useFirestore } from '../contexts/FirestoreContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { createIncidentLog } from '../utils/activityLogger';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import '../styles/mobile.css';

export default function Layout() {
  const location = useLocation();
  const { currentUser, cmtIssues, setCmtIssues } = useAuth();
  const { db } = useFirestore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load initial state from localStorage to match Sidebar
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [incidentData, setIncidentData] = useState(null);
  const [resourceBannerDismissed, setResourceBannerDismissed] = useState(false);
  
  // Lessons Learned Banner state
  const [showLessonsLearnedBanner, setShowLessonsLearnedBanner] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(null);

  // Mobile responsiveness state
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Phase 2: New task assignment notifications
  const { newTaskNotification, dismissNotification, clearNotificationHistory, showAllOutstandingTasks } = useNewTaskNotifications();

      // console.log('🟢 Layout - Current location:', location.pathname);
  
  // Extract incident ID from URL if we're on incident details page
  const isIncidentDetailsPage = location.pathname.startsWith('/incidents/') && location.pathname !== '/incidents';
  const incidentId = isIncidentDetailsPage ? location.pathname.split('/')[2] : null;

  // Fetch incident data for resource suggestions AND lessons learned banner
  useEffect(() => {
    if (!incidentId || !db || !currentUser) {
      setIncidentData(null);
      return;
    }

    console.log('🎯 Layout - Setting up real-time incident listener for ID:', incidentId);
    const incidentRef = doc(db, 'c3_incidents', incidentId);
    
    // Real-time listener for incident changes
    const unsubscribe = onSnapshot(
      incidentRef,
      (incidentDoc) => {
        if (incidentDoc.exists()) {
          const data = incidentDoc.data();
          console.log('🎯 Layout - Real-time incident data update:', data);
          console.log('🎯 Layout - Status changed to:', data.status);
          setIncidentData(data);
        } else {
          console.log('🎯 Layout - Incident not found');
          setIncidentData(null);
        }
      },
      (error) => {
        // Gracefully handle permissions errors - user may not have access to this incident
        if (error.code === 'permission-denied' || error.message?.includes('insufficient permissions')) {
          console.log('🔒 Banner: User does not have permission to view this incident');
        } else {
          console.error('Error in real-time incident listener:', error);
        }
        setIncidentData(null);
      }
    );

    // Cleanup listener on unmount or dependency change
    return () => {
      console.log('🎯 Layout - Cleaning up incident listener');
      unsubscribe();
    };
  }, [incidentId, db, currentUser]);

  // Reset resource banner dismissal when incident changes
  useEffect(() => {
    setResourceBannerDismissed(false);
  }, [incidentId]);

  // Lessons Learned Banner Logic - detect status change to "Resolved"
  useEffect(() => {
    const skipFlag = sessionStorage.getItem(`lessons-learned-skipped-${incidentId}`);
    const shownFlag = sessionStorage.getItem(`lessons-learned-shown-${incidentId}`);
    
    console.log('🚨 BANNER LOGIC CHECK:', {
      currentStatus: incidentData?.status,
      currentStatusLower: incidentData?.status?.toLowerCase(),
      previousStatus: previousStatus,
      previousStatusLower: previousStatus?.toLowerCase(),
      incidentId: incidentId,
      skipFlag: skipFlag,
      shownFlag: shownFlag,
      showingBanner: showLessonsLearnedBanner,
      skipFlagCheck: !skipFlag,
      shownFlagCheck: !shownFlag
    });

    // Only show if status just changed to 'Resolved' in this session
    const currentStatusLower = incidentData?.status?.toLowerCase();
    const previousStatusLower = previousStatus?.toLowerCase();
    
    console.log('🔍 DETAILED CONDITION CHECK:', {
      condition1: currentStatusLower === 'resolved',
      condition2: previousStatusLower !== 'resolved',
      condition3: previousStatus !== null,
      condition4: !skipFlag,
      allConditionsMet: currentStatusLower === 'resolved' && 
                       previousStatusLower !== 'resolved' &&
                       previousStatus !== null &&
                       !skipFlag
    });
    
    if (
      currentStatusLower === 'resolved' && 
      previousStatusLower !== 'resolved' &&
      previousStatus !== null
    ) {
      console.log('🚨🚨🚨 STATUS CHANGED TO RESOLVED - CLEARING SKIP FLAG AND TRIGGERING BANNER! 🚨🚨🚨');
      // Clear any existing skip flag when status changes to resolved
      sessionStorage.removeItem(`lessons-learned-skipped-${incidentId}`);
      setShowLessonsLearnedBanner(true);
    }
    
    setPreviousStatus(incidentData?.status);
  }, [incidentData?.status, incidentId, previousStatus]);

  // Handle resource banner dismissal
  const handleResourceBannerDismiss = () => {
    setResourceBannerDismissed(true);
  };

  // Lessons Learned Banner Handlers
  const handleCompleteLessonsLearned = async () => {
    try {
      // Navigate to the incident details page and switch to lessons learned tab
      const currentPath = location.pathname;
      const isOnIncidentPage = currentPath.startsWith('/incidents/') && currentPath !== '/incidents';
      
      if (!isOnIncidentPage && incidentId) {
        // Navigate to incident page
        window.location.href = `/incidents/${incidentId}`;
        // Add a small delay to allow navigation, then switch to tab
        setTimeout(() => {
          const lessonsLearnedTab = document.querySelector('[data-node-key="lessons-learned"]');
          if (lessonsLearnedTab) {
            lessonsLearnedTab.click();
          }
        }, 500);
      } else {
        // Already on incident page, just switch tab
        const lessonsLearnedTab = document.querySelector('[data-node-key="lessons-learned"]');
        if (lessonsLearnedTab) {
          lessonsLearnedTab.click();
          setTimeout(() => {
            lessonsLearnedTab.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }, 100);
        }
      }
      
      // Hide the banner
      setShowLessonsLearnedBanner(false);
      
      // Log the action
      if (currentUser && incidentId) {
        await createIncidentLog({
          incidentId: incidentId,
          orgId: currentUser.orgId,
          action: 'LESSONS_LEARNED_INITIATED',
          details: 'User started lessons learned process from resolution banner',
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: currentUser.displayName || currentUser.email,
          priority: 'normal',
          metadata: {
            source: 'global_resolution_banner',
            action: 'complete'
          }
        });
      }
      
      console.log('🎯 Lessons Learned: User clicked Complete');
    } catch (error) {
      console.error('Error handling lessons learned completion:', error);
    }
  };

  const handleSkipLessonsLearned = async () => {
    try {
      // Hide the banner
      setShowLessonsLearnedBanner(false);
      
      // Log the skip action
      if (currentUser && incidentId) {
        await createIncidentLog({
          incidentId: incidentId,
          orgId: currentUser.orgId,
          action: 'LESSONS_LEARNED_SKIPPED',
          details: 'User skipped lessons learned from resolution banner',
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: currentUser.displayName || currentUser.email,
          priority: 'normal',
          metadata: {
            source: 'global_resolution_banner',
            action: 'skip'
          }
        });
      }
      
      // Set flag to not show again for this session
      sessionStorage.setItem(`lessons-learned-skipped-${incidentId}`, 'true');
      
      console.log('🎯 Lessons Learned: User clicked Skip');
    } catch (error) {
      console.error('Error handling lessons learned skip:', error);
    }
  };
  
  // 🚀 DYNAMIC BANNER POSITIONING SYSTEM
  const [visibleBanners, setVisibleBanners] = useState(new Set());
  const bannerHeight = 48; // Standard banner height
  
  // Register/unregister banners when they become visible/hidden
  const registerBanner = (bannerId) => {
    setVisibleBanners(prev => new Set([...prev, bannerId]));
  };
  
  const unregisterBanner = (bannerId) => {
    setVisibleBanners(prev => {
      const newSet = new Set(prev);
      newSet.delete(bannerId);
      return newSet;
    });
  };
  
  // Calculate dynamic position for a banner based on visible banners
  const calculateBannerPosition = (bannerId) => {
    const bannerOrder = ['task-notification', 'resource-banner', 'video-call-banner', 'lessons-learned-banner'];
    const bannerIndex = bannerOrder.indexOf(bannerId);
    
    if (bannerIndex === -1) return 0;
    
    let position = 0;
    for (let i = 0; i < bannerIndex; i++) {
      if (visibleBanners.has(bannerOrder[i])) {
        position += bannerHeight;
      }
    }
    
    // console.log(`🎯 Dynamic positioning: ${bannerId} at position ${position}px, visible banners:`, Array.from(visibleBanners));
    return position;
  };

  // Legacy calculation for debugging (can be removed later)
  const taskNotificationHeight = newTaskNotification ? 48 : 0;
  const resourceBannerHeight = (isIncidentDetailsPage && incidentData?.suggestedResources?.length > 0 && !resourceBannerDismissed) ? 48 : 0;
  const lessonsLearnedBannerHeight = (showLessonsLearnedBanner && incidentData?.status?.toLowerCase() === 'resolved') ? 48 : 0;
  const totalNotificationHeight = taskNotificationHeight + resourceBannerHeight + lessonsLearnedBannerHeight;

  // console.log('🎯 Layout - Banner heights:', { 
  //   taskNotificationHeight, 
  //   resourceBannerHeight,
  //   lessonsLearnedBannerHeight,
  //   totalNotificationHeight,
  //   hasTaskNotification: !!newTaskNotification,
  //   hasResourceBanner: !!(isIncidentDetailsPage && incidentData?.suggestedResources?.length > 0),
  //   hasLessonsLearnedBanner: showLessonsLearnedBanner
  // });

  // Mobile and tablet detection and responsive behavior
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 767);
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 767;
      const tablet = window.innerWidth <= 1024 && window.innerWidth > 767;
      setIsMobile(mobile);
      setIsTablet(tablet);
      
      // Close mobile menu when switching back to desktop
      if (!mobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    // Initial check
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Handle mobile menu close on route change
  useEffect(() => {
    if (isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    // Listen for sidebar toggle events
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.isCollapsed);
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);

    // Phase 2: Expose test functions for notification system and login triggers
    window.clearTaskNotificationHistory = clearNotificationHistory;
    window.showAllOutstandingTasks = showAllOutstandingTasks;

    // Cleanup event listener
    return () => {
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
      delete window.clearTaskNotificationHistory;
      delete window.showAllOutstandingTasks;
    };
  }, [clearNotificationHistory, showAllOutstandingTasks]);

  return (
    <div style={{ display: 'flex' }}>
      
      {/* Mobile hamburger menu button - ONLY visible on mobile */}
      {isMobile && (
        <button
          className="mobile-hamburger"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            position: 'fixed',
            top: '12px',
            left: '12px',
            zIndex: 10000,
            backgroundColor: '#39325b',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
        </button>
      )}

      {/* Phase 2: Task assignment notification at the very top */}
      <TaskAssignmentNotification 
        notification={newTaskNotification}
        onDismiss={dismissNotification}
        bannerId="task-notification"
        registerBanner={registerBanner}
        unregisterBanner={unregisterBanner}
        calculatePosition={calculateBannerPosition}
      />

      {/* Video call banner */}
      <VideoCallBanner 
        bannerId="video-call-banner"
        registerBanner={registerBanner}
        unregisterBanner={unregisterBanner}
        calculatePosition={calculateBannerPosition}
      />
      
      {/* Crisis Resource Suggestion Banner - positioned below task notification */}
      {isIncidentDetailsPage && (
        <CrisisResourceSuggestionBanner 
          resources={incidentData?.suggestedResources || []}
          sessionKey={`crisis-resources-${incidentId}`}
          topOffset={taskNotificationHeight}
          onDismiss={handleResourceBannerDismiss}
          bannerId="resource-banner"
          registerBanner={registerBanner}
          unregisterBanner={unregisterBanner}
          calculatePosition={calculateBannerPosition}
        />
      )}

      {/* CMT Issues Banner - positioned at top of content area */}
      {cmtIssues && (
        <CMTIssuesBanner
          cmtIssues={cmtIssues}
          onDismiss={() => setCmtIssues(null)}
          onContactAdmin={() => {
            // Open contact admin functionality
            window.open('mailto:support@crisiscompass.com?subject=CMT Assignment Issues', '_blank');
          }}
        />
      )}

      {/* Lessons Learned Banner - positioned below other banners */}
      {showLessonsLearnedBanner && (
        <LessonsLearnedBanner
          incidentId={incidentId}
          onComplete={handleCompleteLessonsLearned}
          onSkip={handleSkipLessonsLearned}
          topOffset={taskNotificationHeight + resourceBannerHeight}
          bannerId="lessons-learned-banner"
          registerBanner={registerBanner}
          unregisterBanner={unregisterBanner}
          calculatePosition={calculateBannerPosition}
        />
      )}

      {/* Sidebar - Mobile overlay or Desktop fixed */}
      {isMobile ? (
        // Mobile: Overlay sidebar
        <div 
          className={`mobile-sidebar-container ${mobileMenuOpen ? 'mobile-sidebar-open' : ''}`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.3s ease'
          }}
          onClick={(e) => {
            // Close menu when clicking backdrop
            if (e.target === e.currentTarget) {
              setMobileMenuOpen(false);
            }
          }}
        >
          <div 
            className="mobile-sidebar-content"
            style={{
              width: '280px',
              height: '100%',
              backgroundColor: '#39325b',
              position: 'relative',
              overflowY: 'auto'
            }}
          >
            <Sidebar isMobile={true} onMobileNavClick={() => setMobileMenuOpen(false)} />
          </div>
        </div>
      ) : (
        // Desktop: Fixed sidebar - FLEXBOX FIX: Simple container
        <div style={{ 
          position: 'fixed', 
          height: '100vh', // 🔧 FLEXBOX FIX: Clean height - sidebar handles overflow internally
          zIndex: 1000 
        }}>
          <Sidebar />
        </div>
      )}
      
      {/* Main content area - responsive */}
      <main 
        className={isMobile ? 'mobile-layout-main' : ''}
        style={{ 
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? '70px' : '252px'),
          marginTop: '0px', // FIXED: Don't push content down for banners
          minHeight: '100vh', 
          overflow: 'auto',
          transition: isMobile ? 'none' : 'margin-left 0.3s ease',
          flexGrow: 1,
          padding: isMobile ? '0 12px' : '0',
          paddingTop: isMobile ? '60px' : '0' // Account for hamburger button
        }}
      >
        <Outlet />
      </main>
    </div>
  );
} 